<template>
  <div class="urlaubsplaner-content">
    <!-- Abteilungsauswahl und Info-Bereich -->
    <v-card class="info-card mb-4">
      <v-card-text>
        <div class="info-controls">
          <v-select
            v-model="selectedAbteilung"
            :items="abteilungen"
            label="Abteilung wählen"
            variant="outlined"
            :density="isMobile ? 'compact' : 'comfortable'"
            hide-details
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            class="form-field abteilung-select"
            @update:model-value="onAbteilungChange"
          >
            <template #prepend-inner>
              <v-icon color="success">mdi-office-building</v-icon>
            </template>
          </v-select>

          <!-- Kapazitätsanzeige -->
          <div v-if="abteilungRegeln" class="kapazitaet-info">
            <v-chip
              color="info"
              variant="flat"
              :size="isMobile ? 'small' : 'default'"
            >
              <v-icon start :size="isMobile ? 'small' : 'default'">mdi-account-group</v-icon>
              Max. {{ abteilungRegeln.max_gleichzeitig }}{{ isMobile ? ' Pers.' : ' Personen gleichzeitig' }}
            </v-chip>
          </div>

          <!-- Button Gruppe -->
          <div class="action-buttons">
            <!-- Urlaub eintragen Button -->
            <v-btn
              color="success"
              variant="elevated"
              :size="isMobile ? 'small' : 'default'"
              :icon="isMobile ? 'mdi-plus' : undefined"
              :prepend-icon="isMobile ? undefined : 'mdi-plus'"
              @click="openAddUrlaubDialog"
            >
              <span v-if="!isMobile">Urlaub eintragen</span>
            </v-btn>

            <!-- Smart-Planer Button -->
            <v-btn
              color="purple"
              variant="elevated"
              :size="isMobile ? 'small' : 'default'"
              :icon="isMobile ? 'mdi-brain' : undefined"
              :prepend-icon="isMobile ? undefined : 'mdi-brain'"
              @click="showSmartPlanerDialog = true"
            >
              <span v-if="!isMobile">Smart-Planer</span>
            </v-btn>

            <!-- Drucken Button -->
            <v-btn
              color="primary"
              variant="elevated"
              :size="isMobile ? 'small' : 'default'"
              :icon="isMobile ? 'mdi-printer' : undefined"
              :prepend-icon="isMobile ? undefined : 'mdi-printer'"
              @click="printUrlaubsplan"
            >
              <span v-if="!isMobile">Drucken</span>
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Brückentage Statistik & Info -->
    <v-card class="brueckentage-info-card mb-4">
      <v-card-text>
        <div class="d-flex align-center gap-3 flex-wrap">
          <div class="brueckentage-stats">
            <div class="stat-item">
              <v-icon color="purple" size="large">mdi-bridge</v-icon>
              <div class="stat-content">
                <div class="stat-number">{{ brueckentageComputed.length }}</div>
                <div class="stat-label">Brückentage {{ props.selectedYear }}</div>
              </div>
            </div>
            <div class="stat-item">
              <v-icon color="red" size="large">mdi-star</v-icon>
              <div class="stat-content">
                <div class="stat-number">{{ feiertageComputed.length }}</div>
                <div class="stat-label">Feiertage {{ props.selectedYear }}</div>
              </div>
            </div>
            <div class="stat-item">
              <v-icon color="blue" size="large">mdi-bag-suitcase</v-icon>
              <div class="stat-content">
                <div class="stat-number">{{ getJahresFerien(props.selectedYear).length }}</div>
                <div class="stat-label">Ferienzeiten {{ props.selectedYear }}</div>
              </div>
            </div>
          </div>

          <v-spacer></v-spacer>

          <!-- Jahresansicht Button -->
          <v-btn
            color="purple"
            variant="elevated"
            :size="isMobile ? 'small' : 'default'"
            :icon="isMobile ? 'mdi-calendar-star' : undefined"
            :prepend-icon="isMobile ? undefined : 'mdi-calendar-star'"
            @click="showJahresansichtDialog = true"
          >
            <span v-if="!isMobile">Jahresübersicht</span>
          </v-btn>

          <!-- Mitarbeiter-Jahresübersicht Button -->
          <v-btn
            color="info"
            variant="elevated"
            :size="isMobile ? 'small' : 'default'"
            :icon="isMobile ? 'mdi-calendar-account' : undefined"
            :prepend-icon="isMobile ? undefined : 'mdi-calendar-account'"
            @click="showMitarbeiterJahresansicht = true"
          >
            <span v-if="!isMobile">Alle Mitarbeiter</span>
          </v-btn>
        </div>

        <!-- Urlaubsvorschläge (wenn Brückentage vorhanden) -->
        <v-alert
          v-if="besteUrlaubswochen.length > 0"
          type="info"
          variant="tonal"
          class="mt-3"
          density="compact"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-2">mdi-lightbulb-on</v-icon>
            <div>
              <strong>💡 Intelligente Urlaubsplanung:</strong>
              <span class="ml-2">{{ besteUrlaubswochen[0].beschreibung }}</span>
            </div>
          </div>
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Legende -->
    <v-card class="legend-card mb-4">
      <v-card-text>
        <div class="legend-items">
          <div class="legend-item">
            <div class="legend-color" style="background: #F44336"></div>
            <span>Feiertage</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #9C27B0"></div>
            <span>Brückentage</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #FF9800"></div>
            <span>Genehmigter Urlaub</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #FFC107"></div>
            <span>Geplanter Urlaub</span>
          </div>
          <div class="legend-item">
            <div class="legend-color" style="background: #2196F3"></div>
            <span>Beantragter Urlaub</span>
          </div>
          <div class="legend-item">
            <div class="legend-color urlaub-own"></div>
            <span>Eigener Urlaub</span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- Kalender-Grid für Bildschirm-Ansicht -->
    <div class="calendar-grid-container screen-only">
      <!-- Wochentage Header -->
      <div class="calendar-header">
        <div class="week-number-header">KW</div>
        <div
          v-for="day in weekDays"
          :key="day"
          class="week-day-label"
        >
          {{ day }}
        </div>
      </div>

      <!-- Kalenderwochen -->
      <div
        v-for="(week, weekIndex) in weeks"
        :key="weekIndex"
        class="calendar-week"
      >
        <div class="week-number-cell">
          {{ getWeekNumber(week[0].date) }}
        </div>

        <div
          v-for="day in week"
          :key="day.date"
          class="calendar-day urlaub-day"
          :class="{
            'other-month': !day.currentMonth,
            'today': isToday(day.date),
            'weekend': isDayWeekend(day.date),
            'has-feiertag': hasFeiertag(day.date),
            'has-brueckentag': isBrueckentag(day.date),
            'has-urlaub': hasUrlaub(day.date),
            'capacity-warning': isCapacityWarning(day.date),
            'capacity-full': isCapacityFull(day.date)
          }"
          @click="onDayClick(day.date)"
        >
          <div class="day-header">
            <span class="day-number">{{ day.date.getDate() }}</span>
            <v-icon
              v-if="isBrueckentag(day.date)"
              size="small"
              color="purple"
              class="brueckentag-icon"
            >
              mdi-bridge
            </v-icon>
          </div>

          <div class="day-content">
            <!-- Brückentag Hinweis -->
            <div
              v-if="isBrueckentag(day.date)"
              class="urlaub-item brueckentag-item"
            >
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <div class="urlaub-label" v-bind="props">
                    <v-icon size="x-small" class="mr-1">mdi-bridge</v-icon>
                    Brückentag
                  </div>
                </template>
                <div>
                  <strong>💡 Brückentag erkannt!</strong><br>
                  Tag zwischen Feiertag und Wochenende.<br>
                  <em>Perfekt für ein verlängertes Wochenende!</em>
                </div>
              </v-tooltip>
            </div>

            <!-- Feiertage -->
            <div
              v-for="feiertag in getFeiertagOnDate(day.date)"
              :key="feiertag.id"
              class="urlaub-item feiertag-item"
            >
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <div class="urlaub-label" v-bind="props">
                    <v-icon size="x-small" class="mr-1">mdi-star</v-icon>
                    {{ feiertag.name }}
                  </div>
                </template>
                <span>{{ feiertag.name }}</span>
              </v-tooltip>
            </div>

            <!-- Ferienzeiten -->
            <div
              v-for="ferien in getFerienOnDate(day.date)"
              :key="ferien.id"
              class="urlaub-item ferien-item"
            >
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <div class="urlaub-label" v-bind="props">
                    <v-icon size="x-small" class="mr-1">mdi-bag-suitcase</v-icon>
                    {{ ferien.name }}
                  </div>
                </template>
                <div>
                  <strong>{{ ferien.name }}</strong><br>
                  {{ formatDateRange(ferien.start, ferien.end) }}<br>
                  <em>Schulferien NRW</em>
                </div>
              </v-tooltip>
            </div>

            <!-- Urlaube -->
            <div
              v-for="urlaub in getUrlaubeOnDate(day.date)"
              :key="urlaub.id"
              class="urlaub-item"
              :class="{
                'urlaub-genehmigt': urlaub.status === 'genehmigt',
                'urlaub-geplant': urlaub.status === 'geplant',
                'urlaub-beantragt': urlaub.status === 'beantragt',
                'urlaub-abgelehnt': urlaub.status === 'abgelehnt',
                'urlaub-own': urlaub.user_id === authStore.user?.id
              }"
              @click.stop="openEditUrlaubDialog(urlaub)"
            >
              <v-tooltip location="top">
                <template #activator="{ props }">
                  <div class="urlaub-label" v-bind="props">
                    <v-icon size="x-small" class="mr-1">
                      {{ getUrlaubIcon(urlaub) }}
                    </v-icon>
                    {{ urlaub.vollername }}
                  </div>
                </template>
                <div class="urlaub-tooltip">
                  <strong>{{ urlaub.vollername }}</strong><br>
                  {{ formatDateRange(urlaub.von_datum, urlaub.bis_datum) }}<br>
                  <span class="status-badge" :class="`status-${urlaub.status}`">
                    {{ getStatusText(urlaub.status) }}
                  </span>
                  <div v-if="urlaub.kommentar" class="mt-2">
                    {{ urlaub.kommentar }}
                  </div>
                </div>
              </v-tooltip>
            </div>

            <!-- Kapazitätsanzeige -->
            <div v-if="shouldShowCapacity(day.date)" class="capacity-indicator">
              <v-chip
                size="x-small"
                :color="getCapacityColor(day.date)"
                variant="flat"
              >
                {{ getUrlaubeOnDate(day.date).length }}/{{ abteilungRegeln?.max_gleichzeitig || 2 }}
              </v-chip>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PRINT ANSICHT - Wird nur beim Drucken angezeigt -->
    <div class="print-only print-container">
      <!-- Monat-Ansicht -->
      <div v-show="printMode === 'month'" class="print-month-view">
        <div class="print-header">
          <h2>Urlaubsplan - {{ getAbteilungName(selectedAbteilung) }}</h2>
          <p>{{ getMonatName(props.selectedMonth) }} {{ props.selectedYear }}</p>
        </div>

        <div class="print-calendar-grid">
          <!-- Header -->
          <div class="print-calendar-header">
            <div class="print-week-header">KW</div>
            <div class="print-day-header">Mo</div>
            <div class="print-day-header">Di</div>
            <div class="print-day-header">Mi</div>
            <div class="print-day-header">Do</div>
            <div class="print-day-header">Fr</div>
            <div class="print-day-header">Sa</div>
            <div class="print-day-header">So</div>
          </div>

          <!-- Wochen -->
          <div
            v-for="(week, weekIndex) in weeks"
            :key="`print-week-${weekIndex}`"
            class="print-calendar-row"
          >
            <div class="print-week-cell">{{ getWeekNumber(week[0].date) }}</div>
            <div
              v-for="(day, dayIndex) in week"
              :key="`print-day-${dayIndex}`"
              class="print-day-cell"
              :class="{
                'print-weekend': isDayWeekend(day.date),
                'print-other-month': !day.currentMonth,
                'print-today': isToday(day.date)
              }"
            >
              <div class="print-day-number">{{ day.date.getDate() }}</div>
              <div class="print-day-content">
                <!-- Feiertage -->
                <div
                  v-for="feiertag in getFeiertagOnDate(day.date)"
                  :key="`print-ft-${feiertag.id}`"
                  class="print-entry print-feiertag"
                >
                  {{ feiertag.name }}
                </div>
                <!-- Urlaube -->
                <div
                  v-for="urlaub in getUrlaubeOnDate(day.date)"
                  :key="`print-urlaub-${urlaub.id}`"
                  class="print-entry"
                  :class="`print-${urlaub.status}`"
                >
                  {{ urlaub.vollername }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="print-legend">
          <div class="print-legend-item">
            <span class="print-legend-box print-feiertag"></span> Feiertage
          </div>
          <div class="print-legend-item">
            <span class="print-legend-box print-genehmigt"></span> Genehmigt
          </div>
          <div class="print-legend-item">
            <span class="print-legend-box print-beantragt"></span> Beantragt
          </div>
          <div class="print-legend-item">
            <span class="print-legend-box print-geplant"></span> Geplant
          </div>
        </div>
      </div>

      <!-- Jahr-Ansicht (12 Mini-Kalender) -->
      <div v-show="printMode === 'year'" class="print-year-view">
        <div class="print-header">
          <h2>Urlaubsplan {{ props.selectedYear }} - {{ getAbteilungName(selectedAbteilung) }}</h2>
        </div>

        <div class="print-year-grid">
          <div
            v-for="month in 12"
            :key="`year-month-${month}`"
            class="print-mini-month"
          >
            <div class="print-mini-header">
              {{ getMonatName(month) }}
            </div>
            <div class="print-mini-calendar">
              <!-- Mini Kalender für jeden Monat -->
              <div class="print-mini-days">
                <div class="print-mini-day-label">Mo</div>
                <div class="print-mini-day-label">Di</div>
                <div class="print-mini-day-label">Mi</div>
                <div class="print-mini-day-label">Do</div>
                <div class="print-mini-day-label">Fr</div>
                <div class="print-mini-day-label">Sa</div>
                <div class="print-mini-day-label">So</div>
              </div>
              <!-- Tage des Monats - vereinfacht dargestellt -->
              <div class="print-mini-content">
                <div
                  v-for="day in getDaysInMonth(month, props.selectedYear)"
                  :key="`mini-day-${day}`"
                  class="print-mini-day"
                  :class="{
                    'print-mini-has-urlaub': hasUrlaubOnDate(day, month, props.selectedYear)
                  }"
                >
                  {{ day }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="print-legend">
          <div class="print-legend-item">
            <span class="print-legend-box print-mini-has-urlaub"></span> Urlaub eingetragen
          </div>
        </div>
      </div>
    </div>

    <!-- Dialog für Urlaub hinzufügen/bearbeiten -->
    <v-dialog v-model="urlaubDialog" max-width="600px" class="event-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="success" class="mr-2">mdi-beach</v-icon>
          {{ urlaubEditMode ? 'Urlaub bearbeiten' : 'Urlaub eintragen' }}
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="urlaubItem.von_datum"
                label="Von Datum"
                type="date"
                variant="outlined"
                density="comfortable"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                :rules="[v => !!v || 'Von Datum erforderlich']"
              >
                <template #prepend-inner>
                  <v-icon color="success">mdi-calendar-start</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" md="6">
              <v-text-field
                v-model="urlaubItem.bis_datum"
                label="Bis Datum"
                type="date"
                variant="outlined"
                density="comfortable"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                :rules="[v => !!v || 'Bis Datum erforderlich']"
              >
                <template #prepend-inner>
                  <v-icon color="success">mdi-calendar-end</v-icon>
                </template>
              </v-text-field>
            </v-col>
          </v-row>

          <v-select
            v-model="urlaubItem.typ"
            :items="urlaubTypen"
            item-title="text"
            item-value="value"
            label="Urlaubstyp"
            variant="outlined"
            density="comfortable"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            class="form-field"
          >
            <template #prepend-inner>
              <v-icon color="success">mdi-beach</v-icon>
            </template>
          </v-select>

          <v-select
            v-model="urlaubItem.status"
            :items="statusOptionen"
            item-title="text"
            item-value="value"
            label="Status"
            variant="outlined"
            density="comfortable"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            class="form-field"
          >
            <template #prepend-inner>
              <v-icon color="success">mdi-information</v-icon>
            </template>
          </v-select>

          <!-- Hinweis für normale User -->
          <v-alert
            v-if="authStore.user?.admin !== 1 && authStore.user?.admin !== true"
            type="info"
            variant="tonal"
            density="compact"
            class="mt-2"
          >
            <div class="text-caption">
              <v-icon size="small" class="mr-1">mdi-information</v-icon>
              Sie können nur "Geplant" oder "Beantragt" setzen.
              Die Genehmigung erfolgt durch einen Administrator.
            </div>
          </v-alert>

          <!-- Hinweis für Admins bei fremden Urlaubs-Einträgen -->
          <v-alert
            v-if="urlaubEditMode && (authStore.user?.admin === 1 || authStore.user?.admin === true) && urlaubItem.user_id !== authStore.user?.id"
            type="warning"
            variant="tonal"
            density="compact"
            class="mt-2"
          >
            <div class="text-caption">
              <v-icon size="small" class="mr-1">mdi-shield-crown</v-icon>
              <strong>Administrator-Modus:</strong> Sie bearbeiten den Urlaubseintrag eines anderen Benutzers.
              Sie können den Status genehmigen oder ablehnen, aber nicht löschen.
            </div>
          </v-alert>

          <v-textarea
            v-model="urlaubItem.kommentar"
            label="Kommentar (optional)"
            variant="outlined"
            density="comfortable"
            color="success"
            bg-color="rgba(30, 30, 40, 0.5)"
            class="form-field"
            rows="3"
          >
            <template #prepend-inner>
              <v-icon color="success">mdi-comment-text</v-icon>
            </template>
          </v-textarea>

          <!-- Kapazitätswarnung -->
          <v-alert
            v-if="capacityWarning"
            type="warning"
            variant="tonal"
            class="mt-3"
          >
            <strong>Achtung:</strong> Die maximale Kapazität ist an folgenden Tagen erreicht oder überschritten:
            <ul class="mt-2">
              <li v-for="konflikt in capacityWarning.konflikte" :key="konflikt.datum">
                {{ formatDate(konflikt.datum) }}: {{ konflikt.personen.join(', ') }}
              </li>
            </ul>
          </v-alert>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn
            v-if="urlaubEditMode && urlaubItem.user_id === authStore.user?.id"
            variant="text"
            color="error"
            @click="confirmDeleteUrlaub = true"
          >
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn variant="text" color="grey" @click="urlaubDialog = false">
            Abbrechen
          </v-btn>
          <v-btn
            color="success"
            variant="elevated"
            :disabled="!isUrlaubValid"
            @click="saveUrlaub"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog für Löschung -->
    <v-dialog v-model="confirmDeleteUrlaub" max-width="400px" class="confirm-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title warning-header d-flex align-center">
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Urlaub löschen?
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content text-center pa-6">
          <p>Möchten Sie diesen Urlaubseintrag wirklich löschen?</p>
          <p class="text-caption mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="confirmDeleteUrlaub = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="elevated" @click="deleteUrlaubConfirmed">
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Print-Auswahl Dialog -->
    <v-dialog v-model="showPrintDialog" max-width="500px" class="confirm-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-printer</v-icon>
          Urlaubsplan drucken
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-6">
          <p class="mb-4">Wählen Sie die Druckansicht:</p>

          <v-radio-group v-model="printMode" color="primary">
            <v-radio
              value="month"
              color="primary"
            >
              <template #label>
                <div class="d-flex flex-column">
                  <strong>Aktueller Monat</strong>
                  <span class="text-caption text-grey">
                    Detaillierte Ansicht des aktuellen Monats
                  </span>
                </div>
              </template>
            </v-radio>

            <v-radio
              value="year"
              color="primary"
              class="mt-3"
            >
              <template #label>
                <div class="d-flex flex-column">
                  <strong>Komplettes Jahr</strong>
                  <span class="text-caption text-grey">
                    Kompakte Jahresübersicht auf einer Seite
                  </span>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="showPrintDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="elevated" @click="executePrint">
            <v-icon start>mdi-printer</v-icon>
            Drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Smart-Planer Dialog -->
    <v-dialog v-model="showSmartPlanerDialog" max-width="900px" class="smart-planer-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="purple" class="mr-2">mdi-brain</v-icon>
          🤖 Intelligenter Urlaubsplaner {{ selectedJahr }}
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-6">
          <!-- Jahr-Auswahl -->
          <div class="d-flex align-center gap-3 mb-4">
            <v-btn-toggle v-model="selectedJahr" mandatory color="purple" variant="outlined">
              <v-btn :value="2025">2025</v-btn>
              <v-btn :value="2026">2026</v-btn>
              <v-btn :value="2027">2027</v-btn>
            </v-btn-toggle>

            <v-spacer></v-spacer>

            <v-chip color="success" variant="flat">
              <v-icon start>mdi-calculator</v-icon>
              Urlaubstage-Rechner
            </v-chip>
          </div>

          <!-- Urlaubstage Eingabe -->
          <v-card class="mb-4" variant="outlined">
            <v-card-text>
              <div class="d-flex align-center gap-3">
                <v-text-field
                  v-model.number="verfuegbareUrlaubstage"
                  label="Verfügbare Urlaubstage"
                  type="number"
                  variant="outlined"
                  density="comfortable"
                  color="purple"
                  hide-details
                  style="max-width: 200px"
                >
                  <template #prepend-inner>
                    <v-icon color="purple">mdi-calendar-check</v-icon>
                  </template>
                </v-text-field>

                <v-btn
                  color="purple"
                  variant="elevated"
                  @click="berechneOptimaleVerteilung"
                >
                  <v-icon start>mdi-calculator</v-icon>
                  Optimieren
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Optimierungs-Ergebnisse -->
          <v-alert
            v-if="optimierungsErgebnis"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            <div>
              <strong>🎯 Optimale Verteilung gefunden!</strong>
              <p class="mt-2 mb-0">
                Mit <strong>{{ optimierungsErgebnis.benoetigteUrlaubstage }} Urlaubstagen</strong>
                erhalten Sie <strong>{{ optimierungsErgebnis.gesamtFreieTage }} freie Tage</strong>!
                <br>
                <span class="text-caption">
                  Effizienz: {{ optimierungsErgebnis.effizienz }}x ({{ optimierungsErgebnis.gewinnProzent }}% mehr freie Tage)
                </span>
              </p>
            </div>
          </v-alert>

          <!-- Empfohlene Urlaubsblöcke -->
          <div v-if="empfohleneUrlaube.length > 0">
            <h3 class="mb-3">📅 Empfohlene Urlaubsblöcke:</h3>
            <v-card
              v-for="(urlaub, index) in empfohleneUrlaube"
              :key="index"
              class="urlaubs-empfehlung mb-3"
              :class="{ 'top-empfehlung': index === 0 }"
              variant="outlined"
            >
              <v-card-text>
                <div class="d-flex align-center justify-space-between">
                  <div class="flex-grow-1">
                    <div class="d-flex align-center gap-2 mb-2">
                      <v-chip
                        size="small"
                        :color="index === 0 ? 'success' : 'purple'"
                        variant="flat"
                      >
                        #{{ index + 1 }}
                      </v-chip>
                      <strong>{{ urlaub.zeitraum }}</strong>
                      <v-chip size="small" color="orange" variant="flat" v-if="urlaub.enthältBrückentag">
                        <v-icon start size="x-small">mdi-bridge</v-icon>
                        Brückentag
                      </v-chip>
                    </div>
                    <div class="text-caption mb-1">
                      📊 <strong>{{ urlaub.urlaubstage }}</strong> Urlaubstage →
                      <strong>{{ urlaub.freieTage }}</strong> freie Tage
                      (Effizienz: <strong>{{ urlaub.effizienz }}x</strong>)
                    </div>
                    <div class="text-caption text-grey">
                      {{ urlaub.beschreibung }}
                    </div>
                  </div>
                  <v-btn
                    color="success"
                    variant="elevated"
                    size="small"
                    @click="urlaubsblockUebernehmen(urlaub)"
                  >
                    <v-icon start>mdi-calendar-plus</v-icon>
                    Eintragen
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </div>

          <!-- Info wenn keine Urlaubstage eingegeben -->
          <v-alert
            v-else
            type="info"
            variant="tonal"
          >
            Geben Sie Ihre verfügbaren Urlaubstage ein und klicken Sie auf "Optimieren",
            um die beste Verteilung zu berechnen.
          </v-alert>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="showSmartPlanerDialog = false">
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Jahresansicht Dialog -->
    <v-dialog v-model="showJahresansichtDialog" max-width="1200px" class="jahresansicht-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="purple" class="mr-2">mdi-calendar-star</v-icon>
          Jahresübersicht {{ selectedJahr }} - Feiertage & Brückentage
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-6">
          <!-- Jahr-Auswahl -->
          <div class="d-flex align-center gap-3 mb-4">
            <v-btn-toggle v-model="selectedJahr" mandatory color="purple" variant="outlined">
              <v-btn :value="2025">2025</v-btn>
              <v-btn :value="2026">2026</v-btn>
              <v-btn :value="2027">2027</v-btn>
            </v-btn-toggle>

            <v-spacer></v-spacer>

            <v-chip color="purple" variant="flat">
              <v-icon start>mdi-bridge</v-icon>
              {{ getJahresBrueckentage(selectedJahr).length }} Brückentage
            </v-chip>

            <v-chip color="red" variant="flat">
              <v-icon start>mdi-star</v-icon>
              {{ getJahresFeiertage(selectedJahr).length }} Feiertage
            </v-chip>

            <v-chip color="blue" variant="flat">
              <v-icon start>mdi-bag-suitcase</v-icon>
              {{ getJahresFerien(selectedJahr).length }} Ferienzeiten
            </v-chip>
          </div>

          <!-- Beste Urlaubswochen -->
          <v-alert
            v-if="getBesteUrlaubswochen(selectedJahr).length > 0"
            type="success"
            variant="tonal"
            class="mb-4"
          >
            <div>
              <strong>🎯 Beste Urlaubswochen {{ selectedJahr }}:</strong>
              <ul class="mt-2">
                <li v-for="(woche, index) in getBesteUrlaubswochen(selectedJahr).slice(0, 3)" :key="index">
                  <strong>{{ woche.zeitraum }}:</strong> {{ woche.beschreibung }}
                </li>
              </ul>
            </div>
          </v-alert>

          <!-- Monatsübersicht -->
          <div class="jahres-grid">
            <v-card
              v-for="monat in 12"
              :key="`monat-${monat}`"
              class="monat-card"
              elevation="2"
            >
              <v-card-title class="monat-header">
                {{ getMonatName(monat) }}
              </v-card-title>
              <v-card-text class="pa-2">
                <div class="monat-items">
                  <!-- Feiertage -->
                  <div
                    v-for="feiertag in getFeiertageFuerMonat(monat, selectedJahr)"
                    :key="feiertag.id"
                    class="monat-item feiertag-badge"
                  >
                    <v-icon size="x-small">mdi-star</v-icon>
                    {{ formatMonatTag(feiertag.date) }}: {{ feiertag.name }}
                  </div>

                  <!-- Brückentage -->
                  <div
                    v-for="bruecke in getBrueckentageFuerMonat(monat, selectedJahr)"
                    :key="bruecke.datum"
                    class="monat-item bruecke-badge"
                  >
                    <v-icon size="x-small">mdi-bridge</v-icon>
                    {{ formatMonatTag(bruecke.date) }}: Brückentag
                  </div>

                  <!-- Ferienzeiten -->
                  <div
                    v-for="ferien in getFerienFuerMonat(monat, selectedJahr)"
                    :key="ferien.id"
                    class="monat-item ferien-badge"
                  >
                    <v-icon size="x-small">mdi-bag-suitcase</v-icon>
                    {{ ferien.name }} ({{ formatMonatTag(ferien.startDate) }} - {{ formatMonatTag(ferien.endDate) }})
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="showJahresansichtDialog = false">
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Mitarbeiter-Jahresübersicht Dialog -->
    <v-dialog v-model="showMitarbeiterJahresansicht" max-width="1800px" fullscreen class="mitarbeiter-jahresansicht-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="info" class="mr-2">mdi-calendar-account</v-icon>
          Jahresübersicht {{ mitarbeiterJahr }} - Alle Mitarbeiter ({{ getAbteilungName(selectedAbteilung) }})

          <v-spacer></v-spacer>

          <!-- Jahr-Auswahl -->
          <v-btn-toggle v-model="mitarbeiterJahr" mandatory color="info" variant="outlined" class="mr-3">
            <v-btn :value="2024">2024</v-btn>
            <v-btn :value="2025">2025</v-btn>
            <v-btn :value="2026">2026</v-btn>
            <v-btn :value="2027">2027</v-btn>
          </v-btn-toggle>

          <v-btn
            icon="mdi-printer"
            color="primary"
            variant="elevated"
            size="small"
            @click="printMitarbeiterJahresuebersicht"
            class="mr-2"
          ></v-btn>

          <v-btn
            icon="mdi-close"
            variant="text"
            @click="showMitarbeiterJahresansicht = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-4" style="overflow-x: auto;">
          <!-- Loading -->
          <div v-if="loadingMitarbeiterUrlaube" class="text-center py-8">
            <v-progress-circular indeterminate color="info"></v-progress-circular>
            <p class="mt-3">Lade Urlaubsdaten...</p>
          </div>

          <!-- Statistik -->
          <v-card v-if="!loadingMitarbeiterUrlaube" class="mb-4" variant="outlined">
            <v-card-text>
              <div class="d-flex align-center gap-4 flex-wrap">
                <v-chip color="info" variant="flat">
                  <v-icon start>mdi-account-group</v-icon>
                  {{ mitarbeiterList.length }} Mitarbeiter
                </v-chip>
                <v-chip color="success" variant="flat">
                  <v-icon start>mdi-check-circle</v-icon>
                  {{ mitarbeiterUrlaubeGesamt.filter(u => u.status === 'genehmigt').length }} Genehmigt
                </v-chip>
                <v-chip color="warning" variant="flat">
                  <v-icon start>mdi-clock-outline</v-icon>
                  {{ mitarbeiterUrlaubeGesamt.filter(u => u.status === 'beantragt').length }} Beantragt
                </v-chip>
                <v-chip color="grey" variant="flat">
                  <v-icon start>mdi-calendar-outline</v-icon>
                  {{ mitarbeiterUrlaubeGesamt.filter(u => u.status === 'geplant').length }} Geplant
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <!-- Jahresübersicht-Tabelle -->
          <div v-if="!loadingMitarbeiterUrlaube" class="jahresuebersicht-container">
            <table class="jahresuebersicht-table">
              <thead>
                <tr>
                  <th class="mitarbeiter-column sticky-column">Mitarbeiter</th>
                  <th v-for="monat in 12" :key="monat" class="monat-column">
                    {{ getMonatNameKurz(monat) }}
                  </th>
                  <th class="summe-column">∑ Tage</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="mitarbeiter in mitarbeiterList" :key="mitarbeiter.id" class="mitarbeiter-row">
                  <td class="mitarbeiter-column sticky-column">
                    <div class="mitarbeiter-info">
                      <v-icon size="small" class="mr-1">mdi-account</v-icon>
                      <strong>{{ mitarbeiter.vollername }}</strong>
                    </div>
                  </td>
                  <td v-for="monat in 12" :key="monat" class="monat-cell">
                    <div class="monat-urlaube">
                      <div
                        v-for="urlaub in getMitarbeiterUrlaubeImMonat(mitarbeiter.id, monat, mitarbeiterJahr)"
                        :key="urlaub.id"
                        class="urlaub-block"
                        :class="`urlaub-${urlaub.status}`"
                        :title="getUrlaubTooltip(urlaub)"
                      >
                        <span class="urlaub-dates">
                          {{ formatDayMonth(urlaub.von_datum) }} - {{ formatDayMonth(urlaub.bis_datum) }}
                        </span>
                        <v-icon size="x-small" class="ml-1">
                          {{ getUrlaubIcon(urlaub) }}
                        </v-icon>
                      </div>
                    </div>
                  </td>
                  <td class="summe-column">
                    <v-chip size="small" color="info" variant="flat">
                      {{ getMitarbeiterUrlaubstageGesamt(mitarbeiter.id, mitarbeiterJahr) }}
                    </v-chip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Legende -->
          <v-card v-if="!loadingMitarbeiterUrlaube" class="mt-4" variant="outlined">
            <v-card-text>
              <div class="d-flex align-center gap-4 flex-wrap">
                <span class="text-caption"><strong>Legende:</strong></span>
                <v-chip size="small" color="success" variant="flat">
                  <v-icon start size="x-small">mdi-check-circle</v-icon>
                  Genehmigt
                </v-chip>
                <v-chip size="small" color="warning" variant="flat">
                  <v-icon start size="x-small">mdi-clock-outline</v-icon>
                  Beantragt
                </v-chip>
                <v-chip size="small" color="grey" variant="flat">
                  <v-icon start size="x-small">mdi-calendar-outline</v-icon>
                  Geplant
                </v-chip>
                <v-chip size="small" color="error" variant="flat">
                  <v-icon start size="x-small">mdi-close-circle</v-icon>
                  Abgelehnt
                </v-chip>
              </div>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import axios from 'axios';
import Holidays from 'date-holidays';
import { useAuthStore } from '@/stores/auth';
import { API_BASE_URL } from '../config';
import { useToast } from 'vue-toastification';

// Props
const props = defineProps({
  selectedMonth: {
    type: Number,
    required: true
  },
  selectedYear: {
    type: Number,
    required: true
  },
  weeks: {
    type: Array,
    required: true
  },
  weekDays: {
    type: Array,
    required: true
  }
});

// Stores
const authStore = useAuthStore();
const toast = useToast();

// Responsive Design
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

// State
const urlaube = ref([]);
const selectedAbteilung = ref(null);
const abteilungRegeln = ref(null);
const urlaubDialog = ref(false);
const urlaubEditMode = ref(false);
const confirmDeleteUrlaub = ref(false);
const loadingUrlaube = ref(false);
const capacityWarning = ref(null);

const urlaubItem = ref({
  id: null,
  von_datum: '',
  bis_datum: '',
  typ: 'urlaub',
  status: 'geplant',
  kommentar: ''
});

// Konstanten
const abteilungen = [
  'Geschäftsleitung',
  'Support',
  'KC1',
  'KC2',
  'KC3',
  'KC4',
  'Retouren',
  'Lager',
  'Wareneingang/-ausgang',
  'Nicht angegeben'
];

const urlaubTypen = [
  { text: 'Urlaub', value: 'urlaub' },
  { text: 'Sonderurlaub', value: 'sonderurlaub' },
  { text: 'Unbezahlter Urlaub', value: 'unbezahlt' }
];

// Computed
const isUrlaubValid = computed(() => {
  return urlaubItem.value.von_datum && urlaubItem.value.bis_datum;
});

// Status-Optionen basierend auf Benutzerrechten
const statusOptionen = computed(() => {
  const isAdmin = authStore.user?.admin === 1 || authStore.user?.admin === true;

  if (isAdmin) {
    // Admins können alle Status setzen
    return [
      { text: 'Geplant', value: 'geplant' },
      { text: 'Beantragt', value: 'beantragt' },
      { text: 'Genehmigt', value: 'genehmigt' },
      { text: 'Abgelehnt', value: 'abgelehnt' }
    ];
  } else {
    // Normale User können nur "Geplant" und "Beantragt" setzen
    return [
      { text: 'Geplant', value: 'geplant' },
      { text: 'Beantragt', value: 'beantragt' }
    ];
  }
});

// Feiertage aus date-holidays Library
const feiertageComputed = computed(() => {
  const year = props.selectedYear;
  const hd = new Holidays("DE", "NW"); // Deutschland, Nordrhein-Westfalen
  const holidaysList = hd.getHolidays(year) || [];

  return holidaysList
    .filter((holiday) => holiday.type === "public")
    .map((holiday, index) => {
      const date = new Date(holiday.date);
      return {
        id: `holiday-${index}`,
        name: holiday.name,
        datum: formatDateForInput(date),
        date: date
      };
    });
});

// Schulferien NRW (2024-2028)
const schulferienNRW = [
  // 2024
  { name: "Osterferien", start: "2024-03-25", end: "2024-04-06" },
  { name: "Sommerferien", start: "2024-07-08", end: "2024-08-20" },
  { name: "Herbstferien", start: "2024-10-14", end: "2024-10-26" },
  { name: "Weihnachtsferien", start: "2024-12-23", end: "2025-01-06" },
  // 2025
  { name: "Osterferien", start: "2025-04-14", end: "2025-04-26" },
  { name: "Sommerferien", start: "2025-07-16", end: "2025-08-28" },
  { name: "Herbstferien", start: "2025-10-13", end: "2025-10-25" },
  { name: "Weihnachtsferien", start: "2025-12-23", end: "2026-01-06" },
  // 2026
  { name: "Osterferien", start: "2026-03-29", end: "2026-04-09" },
  { name: "Sommerferien", start: "2026-07-15", end: "2026-08-27" },
  { name: "Herbstferien", start: "2026-10-12", end: "2026-10-24" },
  { name: "Weihnachtsferien", start: "2026-12-23", end: "2027-01-06" },
  // 2027
  { name: "Osterferien", start: "2027-04-03", end: "2027-04-15" },
  { name: "Sommerferien", start: "2027-07-14", end: "2027-08-26" },
  { name: "Herbstferien", start: "2027-10-11", end: "2027-10-23" },
  { name: "Weihnachtsferien", start: "2027-12-23", end: "2028-01-06" },
  // 2028
  { name: "Osterferien", start: "2028-03-25", end: "2028-04-06" },
  { name: "Sommerferien", start: "2028-07-12", end: "2028-08-24" },
  { name: "Herbstferien", start: "2028-10-09", end: "2028-10-21" },
  { name: "Weihnachtsferien", start: "2028-12-23", end: "2029-01-06" }
];

// Ferienzeiten für aktuellen Monat/Jahr
const ferienComputed = computed(() => {
  const year = props.selectedYear;
  const month = props.selectedMonth;

  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0);

  return schulferienNRW
    .filter(ferien => {
      const ferienStart = new Date(ferien.start);
      const ferienEnd = new Date(ferien.end);
      // Ferien überschneiden sich mit dem Monat
      return ferienStart <= endDate && ferienEnd >= startDate;
    })
    .map((ferien, index) => ({
      id: `ferien-${index}`,
      name: ferien.name,
      start: ferien.start,
      end: ferien.end,
      startDate: new Date(ferien.start),
      endDate: new Date(ferien.end)
    }));
});

// Brückentage berechnen (Tage zwischen Feiertag und Wochenende)
const brueckentageComputed = computed(() => {
  const brueckentage = [];

  feiertageComputed.value.forEach(feiertag => {
    const feiertagDate = new Date(feiertag.date);
    const dayOfWeek = feiertagDate.getDay(); // 0 = Sonntag, 6 = Samstag

    // Fall 1: Feiertag ist Dienstag -> Montag ist Brückentag
    if (dayOfWeek === 2) {
      const montag = new Date(feiertagDate);
      montag.setDate(montag.getDate() - 1);
      if (!isDayWeekend(montag)) {
        brueckentage.push({
          datum: formatDateForInput(montag),
          date: montag,
          beschreibung: `Brückentag vor ${feiertag.name}`
        });
      }
    }

    // Fall 2: Feiertag ist Donnerstag -> Freitag ist Brückentag
    if (dayOfWeek === 4) {
      const freitag = new Date(feiertagDate);
      freitag.setDate(freitag.getDate() + 1);
      if (!isDayWeekend(freitag)) {
        brueckentage.push({
          datum: formatDateForInput(freitag),
          date: freitag,
          beschreibung: `Brückentag nach ${feiertag.name}`
        });
      }
    }

    // Fall 3: Feiertag ist Montag -> prüfe ob Freitag davor Brückentag ist
    if (dayOfWeek === 1) {
      const freitag = new Date(feiertagDate);
      freitag.setDate(freitag.getDate() - 3); // 3 Tage zurück = Freitag
      const freitagDayOfWeek = freitag.getDay();
      if (freitagDayOfWeek === 5) { // Sicherstellen dass es Freitag ist
        brueckentage.push({
          datum: formatDateForInput(freitag),
          date: freitag,
          beschreibung: `Brückentag vor ${feiertag.name}`
        });
      }
    }

    // Fall 4: Feiertag ist Freitag -> prüfe ob Montag danach Brückentag ist
    if (dayOfWeek === 5) {
      const montag = new Date(feiertagDate);
      montag.setDate(montag.getDate() + 3); // 3 Tage vor = Montag
      const montagDayOfWeek = montag.getDay();
      if (montagDayOfWeek === 1) { // Sicherstellen dass es Montag ist
        brueckentage.push({
          datum: formatDateForInput(montag),
          date: montag,
          beschreibung: `Brückentag nach ${feiertag.name}`
        });
      }
    }
  });

  // Duplikate entfernen
  const uniqueBrueckentage = brueckentage.filter((bt, index, self) =>
    index === self.findIndex((t) => t.datum === bt.datum)
  );

  return uniqueBrueckentage;
});

// Beste Urlaubswochen berechnen (mit Effizienz-Score)
const besteUrlaubswochen = computed(() => {
  const vorschlaege = [];

  brueckentageComputed.value.forEach(bruecke => {
    const brueckeDate = new Date(bruecke.date);
    const dayOfWeek = brueckeDate.getDay();

    // Finde zugehörigen Feiertag
    const feiertag = feiertageComputed.value.find(ft => {
      const ftDate = new Date(ft.date);
      const diff = Math.abs(brueckeDate - ftDate) / (1000 * 60 * 60 * 24);
      return diff <= 4; // Innerhalb von 4 Tagen
    });

    if (feiertag) {
      let urlaubstage = 1; // Der Brückentag selbst
      let freieTage = 0;

      // Berechne Effizienz je nach Konstellation
      if (dayOfWeek === 1) { // Montag
        urlaubstage = 1;
        freieTage = 4; // Sa, So, Mo, Di (Feiertag)
      } else if (dayOfWeek === 5) { // Freitag
        urlaubstage = 1;
        freieTage = 4; // Fr, Sa, So, Mo (Feiertag)
      } else if (dayOfWeek === 2) { // Dienstag (Brückentag vor Mittwoch-Feiertag)
        urlaubstage = 1;
        freieTage = 4;
      } else if (dayOfWeek === 4) { // Donnerstag (Brückentag nach Mittwoch-Feiertag)
        urlaubstage = 1;
        freieTage = 4;
      }

      const effizienz = freieTage / urlaubstage;

      vorschlaege.push({
        datum: bruecke.datum,
        beschreibung: `Mit ${urlaubstage} Urlaubstag → ${freieTage} Tage frei (${feiertag.name})`,
        effizienz,
        zeitraum: `${formatDate(bruecke.datum)} - ${formatDate(feiertag.datum)}`
      });
    }
  });

  // Sortiere nach Effizienz (beste zuerst)
  return vorschlaege.sort((a, b) => b.effizienz - a.effizienz);
});

// Jahresübersicht Funktionen
const getJahresFeiertage = (jahr) => {
  const hd = new Holidays("DE", "NW");
  const holidaysList = hd.getHolidays(jahr) || [];
  return holidaysList
    .filter((holiday) => holiday.type === "public")
    .map((holiday, index) => ({
      id: `holiday-${index}`,
      name: holiday.name,
      datum: formatDateForInput(new Date(holiday.date)),
      date: new Date(holiday.date)
    }));
};

const getJahresFerien = (jahr) => {
  return schulferienNRW
    .filter(ferien => {
      const ferienStart = new Date(ferien.start);
      return ferienStart.getFullYear() === jahr;
    })
    .map((ferien, index) => ({
      id: `ferien-${index}`,
      name: ferien.name,
      start: ferien.start,
      end: ferien.end,
      startDate: new Date(ferien.start),
      endDate: new Date(ferien.end)
    }));
};

const getJahresBrueckentage = (jahr) => {
  const feiertage = getJahresFeiertage(jahr);
  const brueckentage = [];

  feiertage.forEach(feiertag => {
    const feiertagDate = new Date(feiertag.date);
    const dayOfWeek = feiertagDate.getDay();

    if (dayOfWeek === 2) {
      const montag = new Date(feiertagDate);
      montag.setDate(montag.getDate() - 1);
      if (!isDayWeekend(montag)) {
        brueckentage.push({
          datum: formatDateForInput(montag),
          date: montag
        });
      }
    }

    if (dayOfWeek === 4) {
      const freitag = new Date(feiertagDate);
      freitag.setDate(freitag.getDate() + 1);
      if (!isDayWeekend(freitag)) {
        brueckentage.push({
          datum: formatDateForInput(freitag),
          date: freitag
        });
      }
    }

    if (dayOfWeek === 1) {
      const freitag = new Date(feiertagDate);
      freitag.setDate(freitag.getDate() - 3);
      if (freitag.getDay() === 5) {
        brueckentage.push({
          datum: formatDateForInput(freitag),
          date: freitag
        });
      }
    }

    if (dayOfWeek === 5) {
      const montag = new Date(feiertagDate);
      montag.setDate(montag.getDate() + 3);
      if (montag.getDay() === 1) {
        brueckentage.push({
          datum: formatDateForInput(montag),
          date: montag
        });
      }
    }
  });

  return brueckentage.filter((bt, index, self) =>
    index === self.findIndex((t) => t.datum === bt.datum)
  );
};

const getBesteUrlaubswochen = (jahr) => {
  const feiertage = getJahresFeiertage(jahr);
  const brueckentage = getJahresBrueckentage(jahr);
  const vorschlaege = [];

  brueckentage.forEach(bruecke => {
    const brueckeDate = new Date(bruecke.date);
    const feiertag = feiertage.find(ft => {
      const ftDate = new Date(ft.date);
      const diff = Math.abs(brueckeDate - ftDate) / (1000 * 60 * 60 * 24);
      return diff <= 4;
    });

    if (feiertag) {
      vorschlaege.push({
        datum: bruecke.datum,
        beschreibung: `Mit 1 Urlaubstag → 4 Tage frei (${feiertag.name})`,
        zeitraum: `${formatDate(bruecke.datum)}`
      });
    }
  });

  return vorschlaege;
};

const getFeiertageFuerMonat = (monat, jahr) => {
  const feiertage = getJahresFeiertage(jahr);
  return feiertage.filter(ft => {
    const ftDate = new Date(ft.date);
    return ftDate.getMonth() + 1 === monat;
  });
};

const getBrueckentageFuerMonat = (monat, jahr) => {
  const brueckentage = getJahresBrueckentage(jahr);
  return brueckentage.filter(bt => {
    const btDate = new Date(bt.date);
    return btDate.getMonth() + 1 === monat;
  });
};

const getFerienFuerMonat = (monat, jahr) => {
  const ferien = getJahresFerien(jahr);
  return ferien.filter(f => {
    const startMonth = f.startDate.getMonth() + 1;
    const endMonth = f.endDate.getMonth() + 1;
    // Ferien überschneiden sich mit dem Monat
    return (startMonth <= monat && endMonth >= monat) ||
           (startMonth === monat || endMonth === monat);
  });
};

const formatMonatTag = (date) => {
  const d = new Date(date);
  return `${d.getDate()}.${d.getMonth() + 1}.`;
};

// Smart-Planer: Optimale Urlaubsverteilung berechnen
const berechneOptimaleVerteilung = () => {
  const feiertage = getJahresFeiertage(selectedJahr.value);
  const brueckentage = getJahresBrueckentage(selectedJahr.value);
  const moeglicheBlocks = [];

  // Analysiere alle Brückentage-Konstellationen
  brueckentage.forEach(bruecke => {
    const brueckeDate = new Date(bruecke.date);
    const dayOfWeek = brueckeDate.getDay();

    // Finde zugehörigen Feiertag
    const feiertag = feiertage.find(ft => {
      const ftDate = new Date(ft.date);
      const diff = Math.abs(brueckeDate - ftDate) / (1000 * 60 * 60 * 24);
      return diff <= 4;
    });

    if (feiertag) {
      const feiertagDate = new Date(feiertag.date);
      let vonDatum, bisDatum, urlaubstage, freieTage;

      // Berechne optimalen Zeitraum
      if (dayOfWeek === 1) { // Montag (Brückentag vor Dienstag-Feiertag)
        vonDatum = new Date(brueckeDate);
        bisDatum = new Date(feiertagDate);
        urlaubstage = 1;
        freieTage = 4; // Sa, So, Mo (Brücke), Di (Feiertag)
      } else if (dayOfWeek === 5) { // Freitag (Brückentag nach Donnerstag-Feiertag)
        vonDatum = new Date(feiertagDate);
        bisDatum = new Date(brueckeDate);
        urlaubstage = 1;
        freieTage = 4; // Do (Feiertag), Fr (Brücke), Sa, So
      } else if (dayOfWeek === 2) { // Dienstag (vor Mittwoch-Feiertag)
        vonDatum = new Date(brueckeDate);
        bisDatum = new Date(feiertagDate);
        urlaubstage = 1;
        freieTage = 4;
      } else if (dayOfWeek === 4) { // Donnerstag (nach Mittwoch-Feiertag)
        vonDatum = new Date(feiertagDate);
        bisDatum = new Date(brueckeDate);
        urlaubstage = 1;
        freieTage = 4;
      }

      if (vonDatum && bisDatum) {
        const effizienz = freieTage / urlaubstage;
        moeglicheBlocks.push({
          vonDatum: formatDateForInput(vonDatum),
          bisDatum: formatDateForInput(bisDatum),
          zeitraum: `${formatDate(formatDateForInput(vonDatum))} - ${formatDate(formatDateForInput(bisDatum))}`,
          urlaubstage,
          freieTage,
          effizienz,
          enthältBrückentag: true,
          beschreibung: `Inkl. ${feiertag.name} + Brückentag`,
          priority: effizienz * 10 // Für Sortierung
        });
      }
    }
  });

  // Füge auch längere Urlaubsblöcke hinzu (z.B. Sommerurlaub, Weihnachten)
  // Sommer: 2 Wochen im Juli/August
  const sommerStart = new Date(selectedJahr.value, 6, 15); // 15. Juli
  const sommerEnde = new Date(selectedJahr.value, 6, 28); // 28. Juli
  moeglicheBlocks.push({
    vonDatum: formatDateForInput(sommerStart),
    bisDatum: formatDateForInput(sommerEnde),
    zeitraum: `${formatDate(formatDateForInput(sommerStart))} - ${formatDate(formatDateForInput(sommerEnde))}`,
    urlaubstage: 10,
    freieTage: 14,
    effizienz: 1.4,
    enthältBrückentag: false,
    beschreibung: 'Optimaler Sommerurlaub (2 Wochen)',
    priority: 8
  });

  // Weihnachten/Neujahr
  const weihnachtenStart = new Date(selectedJahr.value, 11, 23); // 23. Dezember
  const weihnachtenEnde = new Date(selectedJahr.value + 1, 0, 3); // 3. Januar
  moeglicheBlocks.push({
    vonDatum: formatDateForInput(weihnachtenStart),
    bisDatum: formatDateForInput(weihnachtenEnde),
    zeitraum: `${formatDate(formatDateForInput(weihnachtenStart))} - ${formatDate(formatDateForInput(weihnachtenEnde))}`,
    urlaubstage: 6,
    freieTage: 12,
    effizienz: 2.0,
    enthältBrückentag: true,
    beschreibung: 'Weihnachten/Neujahr optimal nutzen',
    priority: 9
  });

  // Sortiere nach Priorität/Effizienz
  moeglicheBlocks.sort((a, b) => b.priority - a.priority);

  // Wähle beste Kombinationen basierend auf verfügbaren Urlaubstagen
  let verbleibendeTage = verfuegbareUrlaubstage.value;
  const ausgewaehlteBlocks = [];
  let gesamtFreieTage = 0;

  for (const block of moeglicheBlocks) {
    if (block.urlaubstage <= verbleibendeTage) {
      ausgewaehlteBlocks.push(block);
      verbleibendeTage -= block.urlaubstage;
      gesamtFreieTage += block.freieTage;

      // Maximal 5 Blöcke empfehlen
      if (ausgewaehlteBlocks.length >= 5) break;
    }
  }

  empfohleneUrlaube.value = ausgewaehlteBlocks;

  const benoetigteUrlaubstage = verfuegbareUrlaubstage.value - verbleibendeTage;
  const effizienz = (gesamtFreieTage / benoetigteUrlaubstage).toFixed(2);
  const gewinnProzent = (((gesamtFreieTage - benoetigteUrlaubstage) / benoetigteUrlaubstage) * 100).toFixed(0);

  optimierungsErgebnis.value = {
    benoetigteUrlaubstage,
    gesamtFreieTage,
    effizienz,
    gewinnProzent
  };
};

// Urlaubsblock in Dialog übernehmen
const urlaubsblockUebernehmen = (urlaub) => {
  urlaubItem.value.von_datum = urlaub.vonDatum;
  urlaubItem.value.bis_datum = urlaub.bisDatum;
  urlaubItem.value.typ = 'urlaub';
  urlaubItem.value.status = 'geplant';
  urlaubItem.value.kommentar = `Smart-Planer: ${urlaub.beschreibung}`;

  showSmartPlanerDialog.value = false;
  urlaubDialog.value = true;
};

// Methods
const loadUrlaube = async () => {
  if (!selectedAbteilung.value) return;

  loadingUrlaube.value = true;
  try {
    const response = await axios.get(`${API_BASE_URL}/urlaubsplanung.php`, {
      params: {
        action: 'get_urlaube',
        abteilung: selectedAbteilung.value,
        month: props.selectedMonth,
        year: props.selectedYear
      },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      urlaube.value = response.data.urlaube;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Urlaube:', error);
    toast.error('Fehler beim Laden der Urlaube');
  } finally {
    loadingUrlaube.value = false;
  }
};

// Feiertage werden jetzt aus date-holidays Library geladen (siehe feiertageComputed)

const loadAbteilungRegeln = async () => {
  if (!selectedAbteilung.value) return;

  try {
    const response = await axios.get(`${API_BASE_URL}/urlaubsplanung.php`, {
      params: {
        action: 'get_abteilung_regeln',
        abteilung: selectedAbteilung.value
      },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      abteilungRegeln.value = response.data.regeln;
    }
  } catch (error) {
    console.error('Fehler beim Laden der Abteilungsregeln:', error);
  }
};

const onAbteilungChange = () => {
  loadAbteilungRegeln();
  loadUrlaube();
};

const openAddUrlaubDialog = (prefilledDate = null) => {
  urlaubEditMode.value = false;

  // Wenn ein Datum übergeben wurde, verwende es
  const vonDatum = prefilledDate ? formatDateForInput(prefilledDate) : '';
  const bisDatum = prefilledDate ? formatDateForInput(prefilledDate) : '';

  urlaubItem.value = {
    id: null,
    von_datum: vonDatum,
    bis_datum: bisDatum,
    typ: 'urlaub',
    status: 'geplant',
    kommentar: ''
  };

  capacityWarning.value = null;
  urlaubDialog.value = true;
};

const openEditUrlaubDialog = (urlaub) => {
  const isAdmin = authStore.user?.admin === 1 || authStore.user?.admin === true;
  const isOwnUrlaub = urlaub.user_id === authStore.user?.id;

  // Normale User können nur eigene Urlaubseinträge bearbeiten
  if (!isAdmin && !isOwnUrlaub) {
    toast.warning('Sie können nur eigene Urlaubseinträge bearbeiten');
    return;
  }

  // Wenn normaler User einen eigenen genehmigten/abgelehnten Urlaub bearbeiten will
  let statusToUse = urlaub.status;
  if (!isAdmin && isOwnUrlaub && (urlaub.status === 'genehmigt' || urlaub.status === 'abgelehnt')) {
    statusToUse = 'beantragt'; // Fallback auf erlaubten Status
    toast.warning('Der Status wurde bereits von einem Administrator gesetzt und kann nicht geändert werden.');
  }

  urlaubEditMode.value = true;
  urlaubItem.value = {
    id: urlaub.id,
    user_id: urlaub.user_id, // Wichtig für die Löschen-Button-Prüfung
    von_datum: urlaub.von_datum,
    bis_datum: urlaub.bis_datum,
    typ: urlaub.typ,
    status: statusToUse,
    kommentar: urlaub.kommentar || ''
  };
  capacityWarning.value = null;
  urlaubDialog.value = true;
};

const saveUrlaub = async () => {
  const action = urlaubEditMode.value ? 'update_urlaub' : 'add_urlaub';

  try {
    const response = await axios.post(
      `${API_BASE_URL}/urlaubsplanung.php`,
      {
        action,
        ...urlaubItem.value
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success(response.data.message);
      urlaubDialog.value = false;
      await loadUrlaube();
    } else if (response.data.status === 'warning') {
      capacityWarning.value = response.data;
      toast.warning(response.data.message);
    } else {
      toast.error(response.data.message);
    }
  } catch (error) {
    console.error('Fehler beim Speichern des Urlaubs:', error);
    toast.error('Fehler beim Speichern des Urlaubs');
  }
};

const deleteUrlaubConfirmed = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/urlaubsplanung.php`,
      {
        action: 'delete_urlaub',
        id: urlaubItem.value.id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success(response.data.message);
      confirmDeleteUrlaub.value = false;
      urlaubDialog.value = false;
      await loadUrlaube();
    } else {
      toast.error(response.data.message);
    }
  } catch (error) {
    console.error('Fehler beim Löschen des Urlaubs:', error);
    toast.error('Fehler beim Löschen des Urlaubs');
  }
};

const onDayClick = (date) => {
  // Stelle sicher dass date ein Date-Objekt ist
  const clickedDate = date instanceof Date ? date : new Date(date);

  // Öffne Dialog mit vorausgefülltem Datum
  openAddUrlaubDialog(clickedDate);
};

const getFeiertagOnDate = (date) => {
  const dateStr = formatDateForInput(date);
  return feiertageComputed.value.filter(f => f.datum === dateStr);
};

const getFerienOnDate = (date) => {
  const dateStr = formatDateForInput(date);
  return ferienComputed.value.filter(ferien => {
    return dateStr >= ferien.start && dateStr <= ferien.end;
  });
};

const getUrlaubeOnDate = (date) => {
  const dateStr = formatDateForInput(date);
  return urlaube.value.filter(u =>
    dateStr >= u.von_datum && dateStr <= u.bis_datum
  );
};

const hasFeiertag = (date) => {
  return getFeiertagOnDate(date).length > 0;
};

const isBrueckentag = (date) => {
  const dateStr = formatDateForInput(date);
  return brueckentageComputed.value.some(bt => bt.datum === dateStr);
};

const hasUrlaub = (date) => {
  return getUrlaubeOnDate(date).length > 0;
};

const shouldShowCapacity = (date) => {
  return getUrlaubeOnDate(date).length > 0;
};

const getCapacityColor = (date) => {
  const count = getUrlaubeOnDate(date).length;
  const max = abteilungRegeln.value?.max_gleichzeitig || 2;

  if (count >= max) return 'error';
  if (count === max - 1) return 'warning';
  return 'info';
};

const isCapacityWarning = (date) => {
  const count = getUrlaubeOnDate(date).length;
  const max = abteilungRegeln.value?.max_gleichzeitig || 2;
  return count === max - 1;
};

const isCapacityFull = (date) => {
  const count = getUrlaubeOnDate(date).length;
  const max = abteilungRegeln.value?.max_gleichzeitig || 2;
  return count >= max;
};

const getUrlaubIcon = (urlaub) => {
  if (urlaub.status === 'genehmigt') return 'mdi-check-circle';
  if (urlaub.status === 'beantragt') return 'mdi-clock-outline';
  if (urlaub.status === 'abgelehnt') return 'mdi-close-circle';
  return 'mdi-calendar-blank';
};

const getStatusText = (status) => {
  const map = {
    geplant: 'Geplant',
    beantragt: 'Beantragt',
    genehmigt: 'Genehmigt',
    abgelehnt: 'Abgelehnt'
  };
  return map[status] || status;
};

const formatDateForInput = (date) => {
  if (!date) return '';

  // Stelle sicher dass wir ein Date-Objekt haben
  const d = date instanceof Date ? date : new Date(date);

  // Prüfe ob Datum gültig ist
  if (isNaN(d.getTime())) {
    console.error('Ungültiges Datum:', date);
    return '';
  }

  // Verwende lokale Zeit (nicht UTC) um Zeitzone-Probleme zu vermeiden
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE');
};

const formatDateRange = (von, bis) => {
  return `${formatDate(von)} - ${formatDate(bis)}`;
};

const isToday = (date) => {
  const today = new Date();
  return date.toDateString() === today.toDateString();
};

const isDayWeekend = (date) => {
  const day = date.getDay();
  return day === 0 || day === 6;
};

const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

const showPrintDialog = ref(false);
const printMode = ref('month'); // 'month' oder 'year'
const showJahresansichtDialog = ref(false);
const showSmartPlanerDialog = ref(false);
const selectedJahr = ref(2025); // Standard: aktuelles Jahr
const verfuegbareUrlaubstage = ref(30); // Standard: 30 Urlaubstage
const optimierungsErgebnis = ref(null);
const empfohleneUrlaube = ref([]);

// Mitarbeiter-Jahresübersicht
const showMitarbeiterJahresansicht = ref(false);
const mitarbeiterJahr = ref(2025);
const mitarbeiterUrlaubeGesamt = ref([]);
const mitarbeiterList = ref([]);
const loadingMitarbeiterUrlaube = ref(false);

const printUrlaubsplan = () => {
  // Zeige Dialog zur Auswahl zwischen Monats- und Jahresansicht
  showPrintDialog.value = true;
};

const executePrint = async () => {
  showPrintDialog.value = false;
  await nextTick();

  // Kurze Pause, damit v-show vollständig greift
  await new Promise(r => setTimeout(r, 250));

  // HTML des sichtbaren Printcontainers holen
  const printable = document.querySelector('.print-only')?.outerHTML;
  if (!printable) return toast.error('Keine Druckansicht gefunden');

  // Neues Fenster nur mit dem aktiven Inhalt öffnen
  const w = window.open('', '_blank');
  w.document.write(`
    <html>
      <head>
        <title>Druckvorschau</title>
        <style>
          @page { size: A4 landscape; margin: 5mm; }
          html, body {
            width: 287mm; height: 200mm;
            margin: 0; padding: 0;
            background: #fff; color: #000;
            font-family: Arial, sans-serif;
          }
          .print-only { width: 100%; height: 100%; box-sizing: border-box; padding: 5mm; }
          .print-only * { box-shadow: none !important; backdrop-filter: none !important; }
          .screen-only { display: none !important; }

          /* Print Container */
          .print-container {
            width: 100% !important;
            height: 100% !important;
            transform-origin: top left;
            transform: scale(0.955);
          }

          /* Header */
          .print-header {
            text-align: center;
            margin-bottom: 5mm;
          }
          .print-header h2 {
            font-size: 14px;
            margin: 0 0 2mm 0;
            color: #000;
          }
          .print-header p {
            font-size: 11px;
            margin: 0;
            color: #666;
          }

          /* Monat-Ansicht Grid */
          .print-calendar-grid {
            border: 2px solid #000;
            width: 100%;
          }
          .print-calendar-header,
          .print-calendar-row {
            display: grid;
            grid-template-columns: 30px repeat(7, 1fr);
          }
          .print-calendar-header {
            background: #e0e0e0;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-week-header,
          .print-day-header {
            border: 1px solid #000;
            padding: 3px;
            text-align: center;
            font-weight: bold;
            font-size: 10px;
          }
          .print-week-cell {
            border: 1px solid #666;
            padding: 2px;
            text-align: center;
            font-size: 8px;
            background: #f5f5f5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-day-cell {
            border: 1px solid #666;
            padding: 1.5mm;
            min-height: 28mm;
            max-height: 28mm;
            overflow: hidden;
            background: white;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-day-cell.print-weekend {
            background: #f9f9f9;
          }
          .print-day-cell.print-other-month {
            background: #fafafa;
            opacity: 0.5;
          }
          .print-day-cell.print-today {
            border: 2px solid #4CAF50;
          }
          .print-day-number {
            font-weight: bold;
            font-size: 10px;
            margin-bottom: 2px;
          }
          .print-day-content {
            font-size: 7px;
            max-height: 45px;
            overflow: hidden;
          }
          .print-entry {
            padding: 1px 2px;
            margin: 1px 0;
            border-radius: 1px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            border: 1px solid #333;
            font-size: 7px;
            line-height: 1.2;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-entry.print-feiertag {
            background: #ffcdd2;
            color: #c62828;
            font-weight: bold;
            border-color: #c62828;
          }
          .print-entry.print-genehmigt {
            background: #ffe0b2;
            color: #e65100;
            border-color: #e65100;
          }
          .print-entry.print-beantragt {
            background: #bbdefb;
            color: #1565c0;
            border-color: #1565c0;
          }
          .print-entry.print-geplant {
            background: #fff9c4;
            color: #f57f17;
            border-color: #f57f17;
          }

          /* Jahr-Ansicht Grid */
          .print-year-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 4mm;
          }
          .print-mini-month {
            border: 1px solid #000;
            padding: 2mm;
          }
          .print-mini-header {
            text-align: center;
            font-weight: bold;
            font-size: 9px;
            margin-bottom: 3px;
            padding: 2px;
            background: #e0e0e0;
            border: 1px solid #666;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-mini-days,
          .print-mini-content {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            gap: 0.5px;
          }
          .print-mini-day-label {
            text-align: center;
            font-weight: bold;
            font-size: 6px;
            padding: 1px;
            background: #f5f5f5;
            border: 1px solid #ddd;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-mini-day {
            text-align: center;
            padding: 1px;
            font-size: 6px;
            border: 1px solid #ddd;
            min-height: 12px;
            max-height: 12px;
            overflow: hidden;
          }
          .print-mini-day.print-mini-has-urlaub {
            background: #ffe0b2;
            font-weight: bold;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          /* Legende */
          .print-legend {
            margin-top: 3mm;
            display: flex;
            justify-content: center;
            gap: 10px;
          }
          .print-legend-item {
            display: flex;
            align-items: center;
            gap: 4px;
            font-size: 9px;
          }
          .print-legend-box {
            width: 12px;
            height: 12px;
            border: 1px solid #333;
            border-radius: 1px;
            display: inline-block;
          }
          .print-legend-box.print-feiertag {
            background: #ffcdd2;
            border-color: #c62828;
          }
          .print-legend-box.print-genehmigt {
            background: #ffe0b2;
            border-color: #e65100;
          }
          .print-legend-box.print-beantragt {
            background: #bbdefb;
            border-color: #1565c0;
          }
          .print-legend-box.print-geplant {
            background: #fff9c4;
            border-color: #f57f17;
          }
          .print-legend-box.print-mini-has-urlaub {
            background: #ffe0b2;
          }
        </style>
      </head>
      <body>
        ${printable}
      </body>
    </html>
  `);
  w.document.close();
  w.focus();

  // Druckdialog öffnen
  w.print();

  // Fenster schließen nach kurzer Zeit
  setTimeout(() => w.close(), 500);
};

// Hilfsfunktionen für Print-Ansicht
const getAbteilungName = (abteilungId) => {
  const abt = abteilungen.find(a => a.value === abteilungId);
  return abt ? abt.title : abteilungId;
};

const getMonatName = (monthNumber) => {
  const monate = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
  ];
  return monate[monthNumber - 1] || '';
};

const getDaysInMonth = (month, year) => {
  const date = new Date(year, month - 1, 1);
  const days = [];
  while (date.getMonth() === month - 1) {
    days.push(date.getDate());
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const hasUrlaubOnDate = (day, month, year) => {
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  return urlaube.value.some(urlaub => {
    const von = new Date(urlaub.von_datum);
    const bis = new Date(urlaub.bis_datum);
    const check = new Date(dateStr);
    return check >= von && check <= bis;
  });
};

// Mitarbeiter-Jahresübersicht Funktionen
const loadMitarbeiterJahresurlaube = async () => {
  if (!selectedAbteilung.value || !mitarbeiterJahr.value) return;

  loadingMitarbeiterUrlaube.value = true;
  try {
    // Lade alle Urlaube für das gesamte Jahr
    const response = await axios.get(`${API_BASE_URL}/urlaubsplanung.php`, {
      params: {
        action: 'get_jahresurlaube',
        abteilung: selectedAbteilung.value,
        year: mitarbeiterJahr.value
      },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      mitarbeiterUrlaubeGesamt.value = response.data.urlaube || [];

      // Extrahiere eindeutige Mitarbeiterliste
      const mitarbeiterMap = new Map();
      response.data.urlaube.forEach(urlaub => {
        if (!mitarbeiterMap.has(urlaub.user_id)) {
          mitarbeiterMap.set(urlaub.user_id, {
            id: urlaub.user_id,
            vollername: urlaub.vollername
          });
        }
      });
      mitarbeiterList.value = Array.from(mitarbeiterMap.values())
        .sort((a, b) => a.vollername.localeCompare(b.vollername));
    }
  } catch (error) {
    console.error('Fehler beim Laden der Jahresurlaube:', error);
    toast.error('Fehler beim Laden der Urlaubsdaten');
  } finally {
    loadingMitarbeiterUrlaube.value = false;
  }
};

const getMitarbeiterUrlaubeImMonat = (mitarbeiterId, monat, jahr) => {
  const startDate = `${jahr}-${String(monat).padStart(2, '0')}-01`;
  const endDate = new Date(jahr, monat, 0).toISOString().split('T')[0];

  return mitarbeiterUrlaubeGesamt.value.filter(urlaub => {
    if (urlaub.user_id !== mitarbeiterId) return false;

    // Prüfe ob Urlaub den Monat überschneidet
    return urlaub.von_datum <= endDate && urlaub.bis_datum >= startDate;
  });
};

const getMitarbeiterUrlaubstageGesamt = (mitarbeiterId, jahr) => {
  const urlaube = mitarbeiterUrlaubeGesamt.value.filter(u =>
    u.user_id === mitarbeiterId &&
    (u.status === 'genehmigt' || u.status === 'beantragt')
  );

  let gesamt = 0;
  urlaube.forEach(urlaub => {
    const von = new Date(urlaub.von_datum);
    const bis = new Date(urlaub.bis_datum);
    const tage = Math.ceil((bis - von) / (1000 * 60 * 60 * 24)) + 1;
    gesamt += tage;
  });

  return gesamt;
};

const getUrlaubTooltip = (urlaub) => {
  return `${urlaub.vollername}\n${formatDateRange(urlaub.von_datum, urlaub.bis_datum)}\nStatus: ${getStatusText(urlaub.status)}${urlaub.kommentar ? '\n' + urlaub.kommentar : ''}`;
};

const formatDayMonth = (dateStr) => {
  const date = new Date(dateStr);
  return `${date.getDate()}.${date.getMonth() + 1}`;
};

const getMonatNameKurz = (monat) => {
  const monate = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  return monate[monat - 1];
};

const printMitarbeiterJahresuebersicht = async () => {
  await nextTick();

  const printContent = document.querySelector('.jahresuebersicht-container')?.outerHTML;
  if (!printContent) return toast.error('Keine Druckansicht gefunden');

  const w = window.open('', '_blank');
  w.document.write(`
    <html>
      <head>
        <title>Jahresübersicht ${mitarbeiterJahr.value} - ${getAbteilungName(selectedAbteilung.value)}</title>
        <style>
          @page { size: A4 landscape; margin: 10mm; }
          body { font-family: Arial, sans-serif; font-size: 10pt; }
          h1 { text-align: center; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { border: 1px solid #ddd; padding: 4px; text-align: center; }
          th { background: #4CAF50; color: white; font-weight: bold; }
          .mitarbeiter-column { text-align: left; font-weight: bold; }
          .urlaub-block { margin: 2px; padding: 2px 4px; border-radius: 3px; font-size: 8pt; }
          .urlaub-genehmigt { background: #4CAF50; color: white; }
          .urlaub-beantragt { background: #2196F3; color: white; }
          .urlaub-geplant { background: #FFC107; color: black; }
          .urlaub-abgelehnt { background: #9E9E9E; color: white; }
        </style>
      </head>
      <body>
        <h1>Jahresübersicht ${mitarbeiterJahr.value} - ${getAbteilungName(selectedAbteilung.value)}</h1>
        ${printContent}
      </body>
    </html>
  `);
  w.document.close();

  setTimeout(() => {
    w.print();
    w.close();
  }, 500);
};

// Watchers
watch(() => props.selectedMonth, () => {
  loadUrlaube();
  // Feiertage werden automatisch durch feiertageComputed aktualisiert
});

watch(() => props.selectedYear, () => {
  loadUrlaube();
  // Feiertage werden automatisch durch feiertageComputed aktualisiert
});

watch(showMitarbeiterJahresansicht, (newVal) => {
  if (newVal) {
    loadMitarbeiterJahresurlaube();
  }
});

watch(mitarbeiterJahr, () => {
  if (showMitarbeiterJahresansicht.value) {
    loadMitarbeiterJahresurlaube();
  }
});

// Window resize handler
function updateWidth() {
  windowWidth.value = window.innerWidth;
}

// Lifecycle
onMounted(async () => {
  // Lade Benutzer-Abteilung
  if (authStore.user?.abteilung) {
    selectedAbteilung.value = authStore.user.abteilung;
  }

  await loadAbteilungRegeln();
  await loadUrlaube();
  // Feiertage werden automatisch durch feiertageComputed geladen

  // Window resize listener
  window.addEventListener('resize', updateWidth);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateWidth);
});
</script>

<style scoped>
.urlaubsplaner-content {
  width: 100%;
}

.info-card,
.legend-card {
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.info-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.abteilung-select {
  max-width: 300px;
  min-width: 200px;
  flex: 1;
}

.kapazitaet-info {
  display: flex;
  align-items: center;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
}

.legend-items {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-color {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.legend-color.urlaub-own {
  background: linear-gradient(135deg, #4CAF50 50%, #2196F3 50%);
}

/* Kalender Grid Layout */
.calendar-grid-container {
  background: rgba(30, 30, 40, 0.6);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 16px;
  overflow: hidden;
}

.calendar-header {
  display: grid;
  grid-template-columns: 50px repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 8px 4px;
}

.week-number-header {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.week-day-label {
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  padding: 8px 4px;
  text-transform: uppercase;
}

.calendar-week {
  display: grid;
  grid-template-columns: 50px repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 4px;
}

.week-number-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.urlaub-day {
  min-height: 100px;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  padding: 4px;
  position: relative;
  overflow: hidden;
}

.urlaub-day.other-month {
  opacity: 0.4;
  background: rgba(255, 255, 255, 0.01);
}

.urlaub-day.today {
  background: rgba(76, 175, 80, 0.1);
  border: 2px solid rgba(76, 175, 80, 0.5);
}

.urlaub-day.weekend {
  background: rgba(255, 255, 255, 0.01);
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}

.day-number {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  padding: 2px 6px;
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 80px;
  overflow-y: auto;
}

.urlaub-day:hover {
  background: rgba(76, 175, 80, 0.1);
}

.urlaub-day.has-feiertag {
  background: rgba(244, 67, 54, 0.05);
}

.urlaub-day.has-brueckentag {
  background: rgba(156, 39, 176, 0.08);
  border-left: 3px solid #9C27B0;
}

.urlaub-day.capacity-warning {
  border-left: 3px solid #FFC107;
}

.urlaub-day.capacity-full {
  border-left: 3px solid #F44336;
}

.urlaub-item {
  padding: 2px 6px;
  border-radius: 4px;
  margin: 2px 0;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s;
}

.urlaub-item:hover {
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.feiertag-item {
  background: #F44336;
  color: white;
  cursor: default;
}

.ferien-item {
  background: #2196F3;
  color: white;
  cursor: default;
  font-style: italic;
  border-left: 3px solid #1976D2;
}

.brueckentag-item {
  background: #9C27B0;
  color: white;
  cursor: pointer;
  font-weight: 600;
  animation: pulse-bridge 2s ease-in-out infinite;
}

@keyframes pulse-bridge {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.85;
  }
}

.brueckentag-icon {
  animation: bounce-bridge 2s ease-in-out infinite;
}

@keyframes bounce-bridge {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-2px);
  }
}

.urlaub-genehmigt {
  background: #FF9800;
  color: white;
}

.urlaub-geplant {
  background: #FFC107;
  color: #000;
}

.urlaub-beantragt {
  background: #2196F3;
  color: white;
}

.urlaub-abgelehnt {
  background: #9E9E9E;
  color: white;
  opacity: 0.6;
}

.urlaub-own {
  border: 2px solid #4CAF50;
  font-weight: bold;
}

.urlaub-label {
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.capacity-indicator {
  margin-top: 4px;
  display: flex;
  justify-content: center;
}

.urlaub-tooltip {
  font-size: 0.85rem;
}

.status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  margin-top: 4px;
}

.status-geplant {
  background: #FFC107;
  color: #000;
}

.status-beantragt {
  background: #2196F3;
  color: white;
}

.status-genehmigt {
  background: #4CAF50;
  color: white;
}

.status-abgelehnt {
  background: #F44336;
  color: white;
}

/* Brückentage Info Card */
.brueckentage-info-card {
  background: rgba(156, 39, 176, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(156, 39, 176, 0.3);
}

.brueckentage-stats {
  display: flex;
  gap: 30px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-content {
  display: flex;
  flex-direction: column;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1;
}

.stat-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

/* Jahresansicht Dialog */
.jahres-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.monat-card {
  background: rgba(30, 30, 40, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.monat-header {
  background: rgba(156, 39, 176, 0.2);
  padding: 8px 12px;
  font-size: 0.9rem;
  font-weight: 600;
}

.monat-items {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.monat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
}

.feiertag-badge {
  background: rgba(244, 67, 54, 0.2);
  color: #ffcdd2;
}

.bruecke-badge {
  background: rgba(156, 39, 176, 0.2);
  color: #e1bee7;
}

.ferien-badge {
  background: rgba(33, 150, 243, 0.2);
  color: #90caf9;
  font-style: italic;
}

/* Smart-Planer */
.urlaubs-empfehlung {
  background: rgba(30, 30, 40, 0.4);
  border: 1px solid rgba(156, 39, 176, 0.3);
  transition: all 0.3s;
}

.urlaubs-empfehlung:hover {
  background: rgba(30, 30, 40, 0.6);
  border-color: rgba(156, 39, 176, 0.6);
  transform: translateX(4px);
}

.urlaubs-empfehlung.top-empfehlung {
  border: 2px solid rgba(76, 175, 80, 0.6);
  background: rgba(76, 175, 80, 0.1);
}

.urlaubs-empfehlung.top-empfehlung:hover {
  border-color: rgba(76, 175, 80, 0.8);
  background: rgba(76, 175, 80, 0.15);
}

/* Screen-Only Klassen - Beim Drucken verstecken */
.screen-only {
  display: block;
}

/* Print-Only Klassen - Nur beim Drucken anzeigen */
.print-only {
  display: none;
}

/* Mitarbeiter-Jahresübersicht */
.mitarbeiter-jahresansicht-dialog .v-card {
  max-height: 100vh;
  display: flex;
  flex-direction: column;
}

.jahresuebersicht-container {
  overflow-x: auto;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
}

.jahresuebersicht-table {
  width: 100%;
  border-collapse: collapse;
  background: rgba(30, 30, 40, 0.4);
  min-width: 1400px;
}

.jahresuebersicht-table thead {
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(30, 30, 40, 0.95);
}

.jahresuebersicht-table th {
  padding: 12px 8px;
  text-align: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  font-weight: bold;
  color: #fff;
  background: rgba(76, 175, 80, 0.3);
}

.jahresuebersicht-table td {
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  vertical-align: top;
}

.mitarbeiter-column {
  width: 200px;
  text-align: left;
  font-weight: bold;
  background: rgba(30, 30, 40, 0.6);
}

.sticky-column {
  position: sticky;
  left: 0;
  z-index: 5;
  background: rgba(30, 30, 40, 0.95);
}

.monat-column {
  width: 120px;
  min-width: 120px;
}

.summe-column {
  width: 80px;
  text-align: center;
  background: rgba(33, 150, 243, 0.2);
  font-weight: bold;
}

.mitarbeiter-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.monat-urlaube {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 30px;
}

.urlaub-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
}

.urlaub-block:hover {
  transform: scale(1.05);
  filter: brightness(1.2);
}

.urlaub-dates {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mitarbeiter-row:hover {
  background: rgba(255, 255, 255, 0.05);
}

/* ======== Mobile Optimierungen ======== */
@media (max-width: 959px) {
  .info-controls {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .abteilung-select {
    max-width: 100%;
    width: 100%;
  }

  .kapazitaet-info {
    width: 100%;
    justify-content: center;
  }

  .action-buttons {
    margin-left: 0;
    justify-content: center;
    width: 100%;
  }

  .brueckentage-stats {
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }

  .stat-item {
    width: 100%;
    justify-content: center;
  }

  .legend-items {
    justify-content: center;
    gap: 12px;
  }

  .legend-item {
    font-size: 0.85rem;
  }

  .calendar-grid-container {
    padding: 8px;
  }

  .calendar-header {
    grid-template-columns: 40px repeat(7, 1fr);
    gap: 2px;
    padding: 6px 2px;
  }

  .week-number-header {
    font-size: 0.65rem;
  }

  .week-day-label {
    font-size: 0.7rem;
    padding: 6px 2px;
  }

  .calendar-week {
    grid-template-columns: 40px repeat(7, 1fr);
    gap: 2px;
    margin-bottom: 2px;
  }

  .week-number-cell {
    font-size: 0.65rem;
  }

  .urlaub-day {
    min-height: 80px;
  }

  .day-header {
    padding: 4px;
  }

  .day-number {
    font-size: 0.8rem;
  }

  .brueckentag-icon {
    font-size: 12px !important;
  }

  .day-content {
    gap: 2px;
  }

  .urlaub-item {
    padding: 3px 4px;
    font-size: 0.65rem;
  }

  .urlaub-label {
    font-size: 0.65rem;
  }

  .urlaub-label .v-icon {
    font-size: 10px !important;
  }
}

@media (max-width: 600px) {
  .calendar-header {
    grid-template-columns: 30px repeat(7, 1fr);
    gap: 1px;
    padding: 4px 1px;
  }

  .week-number-header {
    font-size: 0.55rem;
  }

  .week-day-label {
    font-size: 0.6rem;
    padding: 4px 1px;
  }

  .calendar-week {
    grid-template-columns: 30px repeat(7, 1fr);
    gap: 1px;
  }

  .week-number-cell {
    font-size: 0.55rem;
  }

  .urlaub-day {
    min-height: 70px;
  }

  .day-header {
    padding: 2px;
  }

  .day-number {
    font-size: 0.7rem;
  }

  .brueckentag-icon {
    font-size: 10px !important;
  }

  .urlaub-item {
    padding: 2px 3px;
    font-size: 0.6rem;
  }

  .urlaub-label {
    font-size: 0.6rem;
  }

  .urlaub-label .v-icon {
    font-size: 8px !important;
  }

  .action-buttons {
    flex-direction: column;
  }

  .action-buttons .v-btn {
    width: 100%;
  }
}
</style>

<!-- GLOBALES PRINT CSS - Vereinfacht, da separates Fenster verwendet wird -->
<style>
@media print {
  body * {
    display: none !important;
    visibility: hidden !important;
  }

  .print-only, .print-only * {
    display: block !important;
    visibility: visible !important;
  }

  .screen-only, .screen-only * {
    display: none !important;
  }

  @page {
    size: A4 landscape;
    margin: 5mm;
  }

  html, body {
    background: #fff !important;
    color: #000 !important;
  }
}
</style>
